/**
 * Utility functions for handling window/iframe navigation
 */

/**
 * Opens a URL in a new tab/window, handling iframe embedding correctly
 * When the app is embedded in an iframe (e.g., Obsidian web panel),
 * this ensures links open in the parent window, not inside the iframe
 */
export function openExternalUrl(url: string): void {
  console.log('[window-utils] Opening external URL:', url)

  try {
    // Check if we're in a Chrome extension
    const isExtension = typeof chrome !== 'undefined' && chrome.tabs

    if (isExtension) {
      // We're in a Chrome extension - use chrome.tabs.create()
      console.log('[window-utils] Using chrome.tabs.create()')
      chrome.tabs.create({ url })
      return
    }

    // Check if we're in an iframe
    const isInIframe = window.self !== window.top

    if (isInIframe && window.top) {
      // We're in an iframe - open in the top window
      console.log('[window-utils] Opening in parent window (iframe context)')
      const result = window.top.open(url, '_blank')
      if (!result) {
        console.warn('[window-utils] Popup blocked! Please allow popups for this site.')
        alert('Popup blockerades! Tillåt popups för denna sida för att öppna externa länkar.')
      }
    } else {
      // Normal browser context
      console.log('[window-utils] Opening in new tab (normal context)')
      const result = window.open(url, '_blank')
      if (!result) {
        console.warn('[window-utils] Popup blocked! Please allow popups for this site.')
        alert('Popup blockerades! Tillåt popups för denna sida för att öppna externa länkar.')
      }
    }
  } catch (error) {
    // Fallback if there's any cross-origin restrictions
    console.warn('[window-utils] Cross-origin restriction, using fallback', error)
    const result = window.open(url, '_blank')
    if (!result) {
      console.error('[window-utils] Failed to open URL even with fallback')
      alert(`Kunde inte öppna länken: ${url}\n\nKopiera URL:en manuellt eller tillåt popups.`)
    }
  }
}

/**
 * Check if the app is currently running inside an iframe
 */
export function isEmbeddedInIframe(): boolean {
  try {
    return window.self !== window.top
  } catch (error) {
    // If we can't access window.top due to cross-origin, assume we're in iframe
    return true
  }
}

/**
 * Get information about the embedding context
 */
export function getEmbeddingContext(): {
  isEmbedded: boolean
  parentOrigin?: string
  referrer?: string
} {
  const isEmbedded = isEmbeddedInIframe()

  return {
    isEmbedded,
    parentOrigin: document.referrer ? new URL(document.referrer).origin : undefined,
    referrer: document.referrer || undefined,
  }
}
